import type { RoleItem, StaffDetail, StaffListItem } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, PageHeader, Panel, formatDateTime } from "../components/ui";
import { useSession } from "../session";

export const statusBadge = (s: Pick<StaffListItem, "status" | "inviteSent">) =>
  s.status === "active" ? <Badge tone="ok">Active</Badge> : s.status === "invited" ? s.inviteSent ? <Badge tone="wait">Invited</Badge> : <Badge tone="off">Not invited yet</Badge> : <Badge tone="off">Deactivated</Badge>;

export function StaffPage() {
  const { me } = useSession();
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles") });
  const [inviting, setInviting] = useState(false);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const roleName = (id: string) => roles.data?.find((r) => r.id === id)?.name ?? "";

  const list = (staff.data ?? []).filter((s) => showDeactivated || s.status !== "deactivated");
  const deactivatedCount = (staff.data ?? []).filter((s) => s.status === "deactivated").length;

  return (
    <div>
      <PageHeader
        title="Staff"
        description="Everyone who can sign in. Roles decide what each person can see and do."
        action={
          <Button variant="primary" onClick={() => setInviting(true)}>
            Invite staff
          </Button>
        }
      />

      {staff.isLoading ? (
        <Loading />
      ) : staff.error ? (
        <Alert>{errorMessage(staff.error)}</Alert>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Name</th>
                <th>Roles</th>
                <th className="hidden sm:table-cell">Status</th>
                <th className="hidden md:table-cell">Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="hover:bg-mist/50">
                  <td>
                    <Link to={`/staff/${s.id}`} className="font-semibold hover:text-brand">
                      {s.firstName} {s.lastName}
                    </Link>
                    {s.id === me?.staff.id && <span className="ml-2 text-sm text-muted">(you)</span>}
                    <div className="text-sm text-muted">{s.email}</div>
                    <div className="mt-1 sm:hidden">{statusBadge(s)}</div>
                  </td>
                  <td className="text-ink-soft">{s.roleIds.map(roleName).filter(Boolean).join(", ") || "No role"}</td>
                  <td className="hidden sm:table-cell">{statusBadge(s)}</td>
                  <td className="hidden text-sm text-ink-soft md:table-cell">{s.lastSignInAt ? formatDateTime(s.lastSignInAt, me?.clinic.timezone) : "Not yet"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {deactivatedCount > 0 && (
        <button className="mt-4 text-sm font-medium text-muted hover:text-ink" onClick={() => setShowDeactivated((v) => !v)}>
          {showDeactivated ? "Hide" : "Show"} deactivated staff ({deactivatedCount})
        </button>
      )}

      {inviting && roles.data && <InviteDialog roles={roles.data} onClose={() => setInviting(false)} />}
    </div>
  );
}

function InviteDialog({ roles, onClose }: { roles: RoleItem[]; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", phone: "" });
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [sendInvite, setSendInvite] = useState(true);
  const invite = useMutation({
    mutationFn: () => post<StaffDetail>("/api/staff", { ...form, phone: form.phone || null, roleIds, sendInvite }),
    onSuccess: async (d) => {
      await qc.invalidateQueries({ queryKey: ["staff"] });
      onClose();
      navigate(`/staff/${d.id}`);
    },
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog
      title="Add staff"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" form="invite-form" type="submit" busy={invite.isPending} disabled={!roleIds.length}>
            {sendInvite ? "Send invite" : "Add staff"}
          </Button>
        </>
      }
    >
      <form
        id="invite-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}
      >
        {invite.error && <Alert>{errorMessage(invite.error)}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" required value={form.firstName} onChange={set("firstName")} autoFocus />
          <Field label="Last name" required value={form.lastName} onChange={set("lastName")} />
        </div>
        <Field label="Email" type="email" required value={form.email} onChange={set("email")} hint={sendInvite ? "They'll get an email with a temporary password and a link to sign in." : undefined} />
        <Field label="Mobile (optional)" type="tel" value={form.phone} onChange={set("phone")} />
        <fieldset>
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Roles</legend>
          <div className="flex flex-col">
            {roles.map((r) => (
              <Checkbox
                key={r.id}
                label={r.name}
                description={r.description}
                checked={roleIds.includes(r.id)}
                onChange={(on) => setRoleIds((ids) => (on ? [...ids, r.id] : ids.filter((x) => x !== r.id)))}
              />
            ))}
          </div>
        </fieldset>
        <Checkbox
          label="Email their invite now"
          description={sendInvite ? undefined : "No email goes out. Send it later with Send invite on their staff page."}
          checked={sendInvite}
          onChange={setSendInvite}
        />
      </form>
    </Dialog>
  );
}
